// ./src/contexts/CurrentEventContext/CurrentEventContext.tsx
//
// Tracks the event whose workspace the user is currently in (derived from the
// /events/:eventId/… URL) and loads a light summary (title + emoji) so the shell
// can show "which event am I in" on every event-scoped page, and the ⌘K palette
// can surface that event's pages. Read-only; refetches when the id changes.

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { OP_RESULT_CODES } from '@sdflc/api-helpers';

import { EventService, Event, EventFeatureUsage } from '@/utils/api';
import { useOptionalEventSetupCatalogContext } from '@/contexts/EventSetupCatalogContext';
import { featuresAfterRules } from '@/utils/eventFeatureRules';
import { EVENT_FEATURE_CODES } from '@/config/eventSetup.generated';

const EVENT_PATH_RE = /^\/events\/([^/]+)(?:\/|$)/;
// Account-scoped routes that live under /events/* but aren't an event id. ⚠ Every static `/events/<word>` route in the
// site map belongs here, or its page asks the API for an event called "<word>" (`/events/create` — the wizard — did, until
// 2026-10-01). `check-nav` sweeps the site map against this.
const NON_EVENT_SEGMENTS = new Set(['add', 'all', 'create', 'templates']);

/** The active :eventId from the path, or null when in the account workspace. */
export const matchCurrentEventId = (pathname: string): string | null => {
  const m = pathname.match(EVENT_PATH_RE);
  if (!m) return null;
  const seg = m[1];
  return seg && !NON_EVENT_SEGMENTS.has(seg) ? seg : null;
};

export interface CurrentEventInfo {
  id: string;
  /** The account the event belongs to — #142: the signed-in person's own event when it is their account. */
  accountId: string | null;
  title: string;
  /** From the event-type catalog — derived at render, so it appears as soon as the catalog does (#62). */
  emoji: string;
  /** The event's type (event_category id). */
  eventCategoryId: string | null;
  /** How guests get in ('link_rsvp' | 'managed_invites' | 'save_the_date' | 'ticketed'). Drives conditional nav (e.g. ticket pages). */
  eventMode: string | null;
  /** 'names' | 'headcount' — whether each attendee's identity is captured. */
  guestTracking: string | null;
  /** #A4.7a — THE currency everything on this event is priced in (tickets, sponsors, rentals, budget, staff pay…). */
  currencyCode: string | null;
  /**
   * ROADMAP A2b #63 — `settings.features.on`, the features this event uses. ⚠ NULL = the event has no record, which
   * means EVERYTHING is on (every event made before #62) — never read it as "nothing".
   */
  featuresOn: string[] | null;
  /** 'single' | 'recurring' | 'agenda' — a REPEATING event always uses its Schedule (its dates live there). */
  scheduleMode: string | null;
  /** #188 / #231 d — in test mode every plan gate allows (going live asks the plan); `useFeatureGate` reads it. */
  isTestMode: boolean;
  /**
   * ROADMAP #231 — 'draft' | 'published' | 'completed' | 'canceled'. A DRAFT tries every plan feature (publishing asks the
   * plan); `useFeatureGate` reads it, so a screen that publishes or unpublishes re-reads the current event.
   */
  eventStatus: string | null;
}

export interface CurrentEventContextValue {
  currentEventId: string | null;
  currentEvent: CurrentEventInfo | null;
  loading: boolean;
  /** Re-read the current event — after a save that changed something shown from here (its currency, its title). */
  reload: () => void;
  /**
   * ROADMAP A2b #63 — which of this event's features have data (`eventFeatureUsage`). ⚠ NULL while it loads and when
   * the read failed: the menu then keeps every page where it always was, because "could not tell" is not "no data".
   */
  featureUsage: EventFeatureUsage | null;
  /**
   * #186 — a read of `featureUsage` for THIS event has answered (or failed) at least once. A screen that MOUNTS by it (the
   * dashboard's self-hiding cards) waits for this, not for `featureUsage`, which stays null on a failure. True where it is
   * never read (an explicit event).
   */
  featureUsageSettled: boolean;
  /** Re-ask which features have data — after the host may have added the first row to a page behind "Show more". */
  refreshFeatureUsage: () => void;
}

const CurrentEventContext = createContext<CurrentEventContextValue>({
  currentEventId: null,
  currentEvent: null,
  loading: false,
  reload: () => undefined,
  featureUsage: null,
  featureUsageSettled: false,
  refreshFeatureUsage: () => undefined,
});

export interface CurrentEventProviderProps {
  children: React.ReactNode;
  /**
   * THE EVENT, named explicitly instead of read from the URL — the Create Event wizard (#62 P6(f)), whose route has no
   * event id but whose steps reuse event pages' drawers that price amounts in "the current event's" currency. `null` = no
   * event yet. Omitted → the event in the URL (the app-wide provider).
   * ⓘ An explicit event has no menu, so its feature usage is never read.
   */
  eventId?: string | null;
}

export const CurrentEventProvider: React.FC<CurrentEventProviderProps> = ({ children, eventId: explicitEventId }) => {
  const location = useLocation();
  const isExplicit = explicitEventId !== undefined;
  const eventId = isExplicit ? explicitEventId : matchCurrentEventId(location.pathname);
  const usageEventId = isExplicit ? null : eventId;

  const [currentEvent, setCurrentEvent] = useState<CurrentEventInfo | null>(null);
  const catalog = useOptionalEventSetupCatalogContext();
  const [loading, setLoading] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const callRef = useRef(0);
  const loadedTickRef = useRef(0);

  useEffect(() => {
    if (!eventId) {
      setCurrentEvent(null);
      return;
    }
    // Don't refetch if we already hold this event — unless a reload was asked for.
    if (currentEvent?.id === eventId && loadedTickRef.current === reloadTick) {
      return;
    }

    loadedTickRef.current = reloadTick;

    const myCall = ++callRef.current;
    setLoading(true);
    EventService.get(eventId)
      .then((result) => {
        if (myCall !== callRef.current) return;
        setLoading(false);
        const row = result.code === OP_RESULT_CODES.OK && Array.isArray(result.data) ? (result.data[0] as Event | undefined) : undefined;
        setCurrentEvent(
          row?.id
            ? {
                id: row.id,
                accountId: row.accountId ?? null,
                title: row.title || 'Untitled event',
                emoji: '📅',
                eventCategoryId: row.eventCategoryId ?? null,
                eventMode: row.eventMode ?? null,
                guestTracking: row.guestTracking ?? null,
                currencyCode: row.currencyCode ?? null,
                scheduleMode: row.scheduleMode ?? null,
                isTestMode: row.isTestMode === true,
                eventStatus: row.eventStatus ?? null,
                featuresOn: Array.isArray(row.settings?.features?.on) ? row.settings!.features!.on.map(String) : null,
              }
            : null,
        );
      })
      .catch(() => {
        if (myCall !== callRef.current) return;
        setLoading(false);
        setCurrentEvent(null);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, reloadTick]);

  const reload = useCallback(() => setReloadTick((n) => n + 1), []);

  // ── ROADMAP A2b #63 — which features have data ─────────────────────────────────────────────────────────────────
  //
  // ⚠ Its own request, its own race guard. It is asked whenever the event is (open, reload) and on its own when the menu
  // asks again (`refreshFeatureUsage`). The answer carries its event id and is only handed out for THAT event, so the
  // previous event's answer can never decide the menu of the one being opened.
  const [usage, setUsage] = useState<EventFeatureUsage | null>(null);
  const [usageTick, setUsageTick] = useState(0);
  /** #186 — the event whose usage read has answered or failed (see `featureUsageSettled`). */
  const [settledFor, setSettledFor] = useState<string | null>(null);
  const usageCallRef = useRef(0);

  useEffect(() => {
    if (!usageEventId) {
      setUsage(null);
      return;
    }

    const myCall = ++usageCallRef.current;
    /**
     * ⚠ A FAILED READ IS NEVER AN EMPTY ANSWER — empty says "nothing has data" and would tuck every page of a feature
     * that is off behind "Show more", rows and all. A failed REFRESH keeps what this event already knew (its data did
     * not vanish because one request did); a failed FIRST read leaves no answer (null), which keeps every page in place.
     */
    // ⚠ #186 review — and what it knew HAS data stays; everything else becomes UNKNOWN. Kept as it was, an old "no rows"
    //   outlived a failed refresh (the first poll added since stayed hidden on the dashboard, which mounts by it).
    const keepOrForget = (prev: EventFeatureUsage | null): EventFeatureUsage | null =>
      prev?.eventId === usageEventId ? { ...prev, unknown: EVENT_FEATURE_CODES.filter((code) => !prev.withData.includes(code)) } : null;
    const settle = () => setSettledFor(usageEventId);

    EventService.featureUsage(usageEventId)
      .then((result) => {
        if (myCall !== usageCallRef.current) {
          return;
        }

        const row = result.code === OP_RESULT_CODES.OK && Array.isArray(result.data) ? (result.data[0] as EventFeatureUsage | undefined) : undefined;

        settle();

        if (row?.eventId) {
          setUsage({ eventId: String(row.eventId), withData: (row.withData ?? []).map(String), unknown: (row.unknown ?? []).map(String) });

          return;
        }

        setUsage(keepOrForget);
      })
      .catch(() => {
        if (myCall === usageCallRef.current) {
          settle();
          setUsage(keepOrForget);
        }
      });
  }, [usageEventId, reloadTick, usageTick]);

  const refreshFeatureUsage = useCallback(() => setUsageTick((n) => n + 1), []);

  // ⚠ The emoji is resolved HERE, at render, not when the event loads: the catalog may still be on its way then, and a
  // value captured in the load callback would stay the fallback for as long as the event stays open.
  const findType = catalog?.findType;
  const parentOf = catalog?.parentOf;
  const rules = catalog?.rules;
  // ⚠⚠ The stored features, RUN THROUGH THE RULES (ROADMAP #63). Details & Settings can change how guests join without
  // touching `settings.features`, so the stored list can lag the mode — an RSVP event switched to tickets must not have
  // its selling pages tucked behind "Show more". Same function the wizard and the Features page save with. A null
  // record stays null (= everything on); with the catalog still loading the stored list is used as it is.
  const shaped = useMemo<CurrentEventInfo | null>(() => {
    if (!currentEvent) {
      return null;
    }

    const type = findType?.(currentEvent.eventCategoryId) ?? null;
    const featuresOn =
      currentEvent.featuresOn === null
        ? null
        : featuresAfterRules(type, type ? (parentOf?.(type) ?? null) : null, rules ?? [], currentEvent.eventMode, currentEvent.featuresOn);

    // ⚠ A REPEATING event uses Sessions whatever its record says — its dates are on the Schedule page, and one switched to
    //   "Repeats" in Details before any date exists would otherwise find that page under "Show more" (review, 2026-10-01).
    const withSchedule =
      featuresOn !== null && currentEvent.scheduleMode === 'recurring' && !featuresOn.includes('sessions') ? [...featuresOn, 'sessions'] : featuresOn;

    return { ...currentEvent, emoji: type?.emoji ?? '📅', featuresOn: withSchedule };
  }, [currentEvent, findType, parentOf, rules]);

  const featureUsage = usageEventId && usage?.eventId === usageEventId ? usage : null;
  const featureUsageSettled = !usageEventId || settledFor === usageEventId;

  const value = useMemo<CurrentEventContextValue>(
    () => ({ currentEventId: eventId, currentEvent: eventId ? shaped : null, loading, reload, featureUsage, featureUsageSettled, refreshFeatureUsage }),
    [eventId, shaped, loading, reload, featureUsage, featureUsageSettled, refreshFeatureUsage],
  );

  return <CurrentEventContext.Provider value={value}>{children}</CurrentEventContext.Provider>;
};

export const useCurrentEvent = (): CurrentEventContextValue => useContext(CurrentEventContext);
